import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiError, api, getToken, setToken } from './api';
import type { Me } from './types';

interface AuthContextValue {
  me: Me | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  /** token recebido na volta do login com Google (fluxo de página inteira) */
  acceptToken: (accessToken: string) => void;
  bootstrap: (input: { email: string; name: string; password: string }) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [token, setTokenState] = useState<string | null>(() => getToken());

  const meQuery = useQuery({
    queryKey: ['me', token],
    queryFn: () => api.get<Me>('/auth/me'),
    enabled: Boolean(token),
    // só a recusa da sessão (401/403) derruba o login. API fora do ar por uns segundos (um deploy, a rede) não desloga
    // ninguém: a tela espera e tenta de novo.
    retry: (_count, err) => !(err instanceof ApiError && (err.status === 401 || err.status === 403)),
    retryDelay: 3000,
  });

  // Token inválido/expirado: limpa e volta para o login.
  useEffect(() => {
    if (token && meQuery.isError) {
      setToken(null);
      setTokenState(null);
    }
  }, [token, meQuery.isError]);

  const accept = useCallback(
    (res: { accessToken: string }) => {
      setToken(res.accessToken);
      qc.clear();
      setTokenState(res.accessToken);
    },
    [qc],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      me: token ? (meQuery.data ?? null) : null,
      loading: Boolean(token) && meQuery.isPending,
      login: async (email, password) => accept(await api.post('/auth/login', { email, password })),
      acceptToken: (accessToken) => accept({ accessToken }),
      bootstrap: async (input) => accept(await api.post('/auth/bootstrap', input)),
      logout: () => {
        setToken(null);
        qc.clear();
        setTokenState(null);
      },
    }),
    [accept, token, meQuery.data, meQuery.isPending, qc],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth fora do AuthProvider');
  return ctx;
}
